import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Captions,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Languages,
  ShieldCheck,
  Sparkles,
  Upload,
} from 'lucide-react';
import { Card } from '../components/UIComponents';
import { SEO, SITE_URL, breadcrumbJsonLd, organizationJsonLd } from '../components/SEO';

const pagePath = '/transcribe-audio-to-text';
const pageUrl = `${SITE_URL}${pagePath}`;
const seoTitle = 'Transcribe Audio to Text Online | OmniScribe';
const seoDescription =
  'Transcribe audio to text online with OmniScribe. Upload MP3, WAV, M4A, MP4, and more to get accurate transcripts, speaker labels, AI summaries, and export files.';

const supportedFormats = ['MP3', 'WAV', 'M4A', 'MP4', 'FLAC', 'OGG', 'OPUS', 'WEBM', 'MOV', 'MKV', 'AAC', 'WMA'];
const exportFormats = ['TXT', 'DOCX', 'PDF', 'SRT', 'VTT', 'CSV'];
const primaryCtaClass =
  'inline-flex h-12 items-center justify-center rounded-lg border border-transparent bg-indigo-600 px-6 text-lg font-medium text-white shadow-sm transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:bg-indigo-600 dark:hover:bg-indigo-500';

const steps = [
  {
    icon: Upload,
    title: 'Upload audio or video',
    description: 'Add an audio file, video file, meeting recording, lecture, interview, podcast, or supported media link.',
  },
  {
    icon: FileText,
    title: 'Generate the transcript',
    description: 'OmniScribe converts speech to editable text with multilingual transcription and optional speaker labels.',
  },
  {
    icon: Download,
    title: 'Edit, summarize, and export',
    description: 'Review the transcript, create AI summaries or key points, then export text, documents, subtitles, or CSV files.',
  },
];

const featureBlocks = [
  {
    icon: Languages,
    title: '98+ languages',
    description: 'Transcribe global audio content including meetings, interviews, lectures, videos, voice notes, and podcasts.',
  },
  {
    icon: Captions,
    title: 'Subtitle-ready exports',
    description: 'Create transcript files for publishing, captions, research notes, repurposed content, and searchable archives.',
  },
  {
    icon: Sparkles,
    title: 'AI analysis included',
    description: 'Turn long recordings into summaries, key points, and mind maps after the audio is converted to text.',
  },
  {
    icon: ShieldCheck,
    title: 'Private file workflow',
    description: 'Files use private storage and signed access links instead of exposing public media URLs by default.',
  },
];

const useCases = [
  'Meeting recordings',
  'Podcast episodes',
  'Research interviews',
  'Sales calls',
  'Lectures and classes',
  'Voice memos',
  'Webinar recordings',
  'Video captions',
];

const faqItems = [
  {
    question: 'How do I transcribe audio to text online?',
    answer:
      'Upload your audio or video file to OmniScribe, start transcription, then review the editable transcript in your workspace. You can export the result as TXT, DOCX, PDF, SRT, VTT, or CSV.',
  },
  {
    question: 'Which audio formats can OmniScribe transcribe?',
    answer:
      'OmniScribe supports common audio and video formats including MP3, WAV, M4A, MP4, FLAC, OGG, OPUS, WEBM, MOV, MKV, AAC, and WMA.',
  },
  {
    question: 'Can I transcribe audio with multiple speakers?',
    answer:
      'Yes. OmniScribe supports speaker labels so interviews, podcasts, calls, and meetings are easier to scan and edit after transcription.',
  },
  {
    question: 'Can I use the transcript for captions?',
    answer:
      'Yes. You can export subtitle files such as SRT and VTT for video captions, along with document and plain text formats.',
  },
];

const jsonLd = [
  organizationJsonLd,
  breadcrumbJsonLd([
    { name: 'Home', path: '/' },
    { name: 'Transcribe Audio to Text', path: pagePath },
  ]),
  {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'OmniScribe Audio to Text Transcription',
    url: pageUrl,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: seoDescription,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free monthly transcription minutes are available without a credit card.',
    },
    featureList: [
      'Transcribe audio to text',
      'Transcribe video to text',
      'Speaker labels',
      'AI summaries',
      'TXT, DOCX, PDF, SRT, VTT, and CSV exports',
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to transcribe audio to text with OmniScribe',
    description: 'Upload an audio file, generate an AI transcript, then edit and export the text.',
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.title,
      text: step.description,
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  },
];

const TranscribeAudioToText: React.FC = () => {
  return (
    <div className="bg-white text-slate-900 dark:bg-zinc-950 dark:text-white">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path={pagePath}
        keywords={[
          'transcribe audio to text',
          'audio to text',
          'audio transcription',
          'speech to text',
          'voice to text',
          'MP3 to text',
          'WAV to text',
          'M4A to text',
          'meeting transcription',
          'podcast transcription',
        ]}
        jsonLd={jsonLd}
      />

      <section className="border-b border-slate-100 bg-slate-50/70 py-16 dark:border-zinc-800 dark:bg-zinc-900/40">
        <div className="container mx-auto grid max-w-6xl gap-10 px-4 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3 py-1 text-sm font-semibold text-emerald-700 shadow-sm dark:border-emerald-500/20 dark:bg-zinc-900 dark:text-emerald-300">
              <Clock className="h-4 w-4" />
              Online audio transcription
            </div>
            <h1 className="h-hero max-w-3xl">
              Transcribe audio to text online
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 dark:text-zinc-300">
              Convert audio recordings, videos, meetings, lectures, interviews, podcasts, and voice notes into accurate editable text. OmniScribe supports speaker labels, AI summaries, and exports for captions, documents, and research workflows.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/register" className={primaryCtaClass}>
                Start transcribing free
                <ArrowRight className="ms-2 h-5 w-5" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex h-12 items-center justify-center rounded-lg border border-slate-300 px-6 text-lg font-medium text-slate-700 transition-colors hover:bg-white dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-900"
              >
                View plans
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-2 text-sm font-medium text-slate-600 dark:text-zinc-300">
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 dark:border-zinc-800 dark:bg-zinc-900">25+ media formats</span>
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 dark:border-zinc-800 dark:bg-zinc-900">98+ languages</span>
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 dark:border-zinc-800 dark:bg-zinc-900">6 export formats</span>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/70 dark:border-zinc-800 dark:bg-zinc-900 dark:shadow-none">
            <div className="mb-5 flex items-center justify-between border-b border-slate-100 pb-4 dark:border-zinc-800">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-zinc-400">Transcript preview</p>
                <h2 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">Interview audio converted to text</h2>
              </div>
              <FileText className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="space-y-4 text-sm leading-6 text-slate-700 dark:text-zinc-300">
              <div>
                <p className="font-semibold text-slate-950 dark:text-white">Speaker 1</p>
                <p>Welcome back. Today we are reviewing the launch timeline, customer questions, and follow-up tasks.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-950 dark:text-white">Speaker 2</p>
                <p>The transcript makes it easy to pull action items, create a summary, and share notes with the team.</p>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-3 text-center text-sm">
              {exportFormats.slice(0, 6).map((format) => (
                <span key={format} className="rounded-lg bg-slate-100 px-3 py-2 font-semibold text-slate-700 dark:bg-zinc-800 dark:text-zinc-200">
                  {format}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="grid gap-6 md:grid-cols-3">
            {steps.map((step, index) => (
              <Card key={step.title} className="p-6">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300">
                  <step.icon className="h-6 w-6" />
                </div>
                <p className="mb-2 text-sm font-bold text-indigo-600 dark:text-indigo-300">Step {index + 1}</p>
                <h2 className="text-xl font-bold text-slate-950 dark:text-white">{step.title}</h2>
                <p className="mt-3 leading-7 text-slate-600 dark:text-zinc-400">{step.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-100 bg-slate-50 py-16 dark:border-zinc-800 dark:bg-zinc-900/50">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-950 dark:text-white">Built for everyday transcription work</h2>
              <p className="mt-4 leading-7 text-slate-600 dark:text-zinc-400">
                Use OmniScribe when you need more than a raw transcript. Convert speech to text, clean up the result, extract highlights, and export files that fit your publishing, research, or team workflow.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {featureBlocks.map((feature) => (
                <div key={feature.title} className="rounded-xl border border-slate-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-950">
                  <feature.icon className="mb-4 h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  <h3 className="font-bold text-slate-950 dark:text-white">{feature.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-zinc-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto grid max-w-6xl gap-10 px-4 lg:grid-cols-2">
          <div>
            <h2 className="text-3xl font-bold text-slate-950 dark:text-white">Supported audio and video formats</h2>
            <p className="mt-4 leading-7 text-slate-600 dark:text-zinc-400">
              Upload common recording formats directly, or use the browser audio converter when a file needs to be prepared before transcription.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {supportedFormats.map((format) => (
                <span key={format} className="rounded-md bg-slate-100 px-3 py-2 text-sm font-bold text-slate-700 dark:bg-zinc-900 dark:text-zinc-200">
                  {format}
                </span>
              ))}
            </div>
            <Link
              to="/tools/audio-converter"
              className="mt-6 inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-700 dark:text-indigo-300 dark:hover:text-indigo-200"
            >
              Prepare audio files before transcription
              <ArrowRight className="ms-1 h-4 w-4" />
            </Link>
          </div>
          <div>
            <h2 className="text-3xl font-bold text-slate-950 dark:text-white">Popular audio to text use cases</h2>
            <p className="mt-4 leading-7 text-slate-600 dark:text-zinc-400">
              Teams, creators, researchers, students, and operators use transcripts to search spoken content, publish faster, and turn recordings into reusable knowledge.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {useCases.map((useCase) => (
                <div key={useCase} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-sm font-semibold text-slate-700 dark:text-zinc-200">{useCase}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-100 bg-slate-50 py-16 dark:border-zinc-800 dark:bg-zinc-900/50">
        <div className="container mx-auto max-w-4xl px-4">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-slate-950 dark:text-white">Audio to text FAQ</h2>
            <p className="mt-4 text-slate-600 dark:text-zinc-400">Common questions about transcribing audio files with OmniScribe.</p>
          </div>
          <div className="mt-10 space-y-4">
            {faqItems.map((faq) => (
              <div key={faq.question} className="rounded-xl border border-slate-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-950">
                <h3 className="text-lg font-bold text-slate-950 dark:text-white">{faq.question}</h3>
                <p className="mt-3 leading-7 text-slate-600 dark:text-zinc-400">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <h2 className="text-3xl font-bold text-slate-950 dark:text-white">Start with your next recording</h2>
          <p className="mx-auto mt-4 max-w-2xl leading-7 text-slate-600 dark:text-zinc-400">
            Upload an audio or video file and turn spoken content into searchable, editable text with export-ready outputs.
          </p>
          <div className="mt-8 flex justify-center">
            <Link to="/register" className={primaryCtaClass}>
              Transcribe audio to text
              <ArrowRight className="ms-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TranscribeAudioToText;
